import { HttpClient } from '@angular/common/http';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { Generator, GeneratorUnit } from '../../../models/api-response.model';

type GeneratorDashboardStat = {
  label: string;
  value: string;
  suffix?: string;
  icon: string;
  tone: 'blue' | 'green' | 'red' | 'amber' | 'violet';
};

type GeneratorMockRow = {
  code: string;
  name: string;
  companyCode: string;
  companyName: string;
  qcaCode: string;
  qcaName: string;
  pssCode: string;
  pssName: string;
  technology: 'Solar' | 'Wind' | 'Hybrid';
  organizationType: string;
  sourceType: string;
  fuelType: string;
  parentCompany: string;
  scadaStationCode: string;
  connectingSubstation: string;
  connectingVoltageLevelKv: string;
  voltageLevelKv: string;
  installedCapacityMw: number;
  generatorCapacityMw: number;
  commissionDate: string;
  status: 'Operational' | 'Under Outage' | 'Standby';
  remarks: string;
};

type GeneratorMockUnit = {
  code: string;
  uniqueNo: string;
  model: string;
  generator: string;
  purchaseMode: string;
  installedCapacityMw: number;
  status: 'Operational' | 'Under Outage' | 'Standby';
  developerName: string;
  commissionDate: string;
};

type GeneratorMockData = {
  header: {
    title: string;
    description: string;
  };
  rows: GeneratorMockRow[];
  unitCatalog: GeneratorMockUnit[];
  generatorUnitMappings: Record<string, string[]>;
};

type LocalState = {
  generators: Generator[];
  units: GeneratorUnit[];
  unitCodeById: Record<number, string>;
  mappings: Record<number, number[]>;
};

const DEFAULT_DATA: GeneratorMockData = {
  header: {
    title: 'Manage Generators',
    description: 'Review generator registrations and manage linked generation units.'
  },
  rows: [],
  unitCatalog: [],
  generatorUnitMappings: {}
};

@Component({
  selector: 'app-generators',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './generators.component.html',
  styleUrls: ['./generators.component.scss']
})
export class GeneratorsComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpClient);

  readonly generators = signal<Generator[]>([]);
  readonly searchTerm = signal('');
  readonly currentPage = signal(1);
  readonly pageSize = signal(10);
  readonly totalItems = signal(0);
  readonly totalPages = signal(1);
  readonly isFormOpen = signal(false);
  readonly editingGeneratorId = signal<number | null>(null);
  readonly selectedGenerator = signal<Generator | null>(null);
  readonly assignedUnits = signal<GeneratorUnit[]>([]);
  readonly availableUnits = signal<GeneratorUnit[]>([]);
  readonly isUnitsModalOpen = signal(false);
  readonly isAssignModalOpen = signal(false);
  readonly availableUnitSearch = signal('');
  readonly toast = signal<{ message: string; type: 'success' | 'error' } | null>(null);
  readonly header = signal(DEFAULT_DATA.header);

  private readonly allGenerators = signal<Generator[]>([]);
  private readonly allUnits = signal<GeneratorUnit[]>([]);
  private readonly generatorUnitMappings = signal<Record<number, number[]>>({});
  private readonly unitCodeById = signal<Record<number, string>>({});

  readonly pageNumbers = computed(() => Array.from({ length: this.totalPages() }, (_, index) => index + 1));
  readonly showingStart = computed(() => (this.totalItems() === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1));
  readonly showingEnd = computed(() => (this.totalItems() === 0 ? 0 : Math.min(this.currentPage() * this.pageSize(), this.totalItems())));
  readonly stats = computed<GeneratorDashboardStat[]>(() => {
    const generators = this.allGenerators();
    const units = this.allUnits();
    const assignedUnitIds = new Set(Object.values(this.generatorUnitMappings()).flat());
    const totalCapacity = generators.reduce((total, generator) => total + Number(generator.installed_capacity_mw || 0), 0);

    return [
      { label: 'Total Generators', value: String(generators.length), icon: '◫', tone: 'blue' },
      { label: 'Total Units', value: String(units.length), icon: '▦', tone: 'violet' },
      { label: 'Assigned Units', value: String(assignedUnitIds.size), icon: '◎', tone: 'green' },
      { label: 'Available Units', value: String(units.filter((unit) => !assignedUnitIds.has(unit.id)).length), icon: '◌', tone: 'amber' },
      { label: 'Installed Capacity', value: this.formatCapacity(totalCapacity), suffix: 'MW', icon: 'ϟ', tone: 'blue' }
    ];
  });

  readonly form = this.fb.nonNullable.group({
    generator_code: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(80)]],
    name_of_power_plant: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(160)]],
    organization_type: ['', [Validators.required, Validators.minLength(1)]],
    source_type: ['', [Validators.required, Validators.minLength(1)]],
    fuel_type: [''],
    parent_company: [''],
    scada_station_code: [''],
    connecting_substation: [''],
    connecting_voltage_level_kv: [''],
    voltage_level_kv: [''],
    installed_capacity_mw: [''],
    generator_capacity_mw: [''],
    no_of_units: [0, [Validators.required, Validators.min(0)]],
    log_sheet: [''],
    file_path: [''],
    other_parameter: ['']
  });

  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.loadMockData();
  }

  openCreateForm(): void {
    this.showToast(null);
    this.editingGeneratorId.set(null);
    this.form.reset({
      generator_code: '',
      name_of_power_plant: '',
      organization_type: '',
      source_type: '',
      fuel_type: '',
      parent_company: '',
      scada_station_code: '',
      connecting_substation: '',
      connecting_voltage_level_kv: '',
      voltage_level_kv: '',
      installed_capacity_mw: '',
      generator_capacity_mw: '',
      no_of_units: 0,
      log_sheet: '',
      file_path: '',
      other_parameter: ''
    });
    this.isFormOpen.set(true);
  }

  edit(generator: Generator): void {
    this.showToast(null);
    this.editingGeneratorId.set(generator.id);
    this.form.reset({
      generator_code: generator.generator_code,
      name_of_power_plant: generator.name_of_power_plant,
      organization_type: generator.organization_type,
      source_type: generator.source_type,
      fuel_type: generator.fuel_type || '',
      parent_company: generator.parent_company || '',
      scada_station_code: generator.scada_station_code || '',
      connecting_substation: generator.connecting_substation || '',
      connecting_voltage_level_kv: generator.connecting_voltage_level_kv || '',
      voltage_level_kv: generator.voltage_level_kv || '',
      installed_capacity_mw: generator.installed_capacity_mw || '',
      generator_capacity_mw: generator.generator_capacity_mw || '',
      no_of_units: generator.no_of_units,
      log_sheet: generator.log_sheet || '',
      file_path: generator.file_path || '',
      other_parameter: generator.other_parameter || ''
    });
    this.isFormOpen.set(true);
  }

  closeForm(): void {
    this.isFormOpen.set(false);
    this.editingGeneratorId.set(null);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const rawValue = this.form.getRawValue();
    const existingId = this.editingGeneratorId();
    const duplicate = this.allGenerators().some((generator) =>
      generator.generator_code.toLowerCase() === rawValue.generator_code.trim().toLowerCase() && generator.id !== existingId
    );

    if (duplicate) {
      this.showToast('Generator code already exists.', 'error');
      return;
    }

    const nextId = existingId ?? this.nextGeneratorId();
    const nextGenerator: Generator = {
      id: nextId,
      generator_code: rawValue.generator_code.trim(),
      name_of_power_plant: rawValue.name_of_power_plant.trim(),
      organization_type: rawValue.organization_type.trim(),
      source_type: rawValue.source_type.trim(),
      fuel_type: rawValue.fuel_type.trim() || null,
      parent_company: rawValue.parent_company.trim() || null,
      scada_station_code: rawValue.scada_station_code.trim() || null,
      connecting_substation: rawValue.connecting_substation.trim() || null,
      connecting_voltage_level_kv: rawValue.connecting_voltage_level_kv.trim() || null,
      voltage_level_kv: rawValue.voltage_level_kv.trim() || null,
      installed_capacity_mw: rawValue.installed_capacity_mw.trim() || null,
      generator_capacity_mw: rawValue.generator_capacity_mw.trim() || null,
      no_of_units: this.generatorUnitMappings()[nextId]?.length || rawValue.no_of_units || 0,
      log_sheet: rawValue.log_sheet.trim() || null,
      file_path: rawValue.file_path.trim() || null,
      other_parameter: rawValue.other_parameter.trim() || null,
      created_at: null,
      updated_at: null,
      is_deleted: false,
      unit_count: this.generatorUnitMappings()[nextId]?.length || rawValue.no_of_units || 0
    };

    if (existingId === null) {
      this.allGenerators.update((generators) => [nextGenerator, ...generators]);
      this.generatorUnitMappings.update((mappings) => ({ ...mappings, [nextId]: [] }));
    } else {
      this.allGenerators.update((generators) =>
        generators.map((generator) => generator.id === existingId ? nextGenerator : generator)
      );
    }

    this.showToast(existingId === null ? 'Generator created successfully.' : 'Generator updated successfully.', 'success');
    this.closeForm();
    this.loadGenerators(existingId === null ? 1 : this.currentPage());
  }

  remove(generator: Generator): void {
    if (!confirm(`Delete generator "${generator.name_of_power_plant}"?`)) {
      return;
    }

    this.allGenerators.update((generators) => generators.filter((item) => item.id !== generator.id));
    this.generatorUnitMappings.update((mappings) => {
      const next = { ...mappings };
      delete next[generator.id];
      return next;
    });

    this.showToast('Generator deleted successfully.', 'success');
    this.loadGenerators(this.currentPage());
  }

  onSearch(value: string): void {
    this.searchTerm.set(value);
    this.loadGenerators(1);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages()) {
      return;
    }
    this.loadGenerators(page);
  }

  openUnitsModal(generator: Generator): void {
    this.selectedGenerator.set(generator);
    this.isUnitsModalOpen.set(true);
    this.isAssignModalOpen.set(false);
    this.availableUnitSearch.set('');
    this.loadAssignedUnits(generator.id);
    this.loadAvailableUnits(generator.id);
  }

  closeUnitsModal(): void {
    this.isUnitsModalOpen.set(false);
    this.isAssignModalOpen.set(false);
    this.selectedGenerator.set(null);
    this.assignedUnits.set([]);
    this.availableUnits.set([]);
    this.availableUnitSearch.set('');
  }

  openAssignModal(): void {
    const generator = this.selectedGenerator();
    if (!generator) {
      return;
    }
    this.isAssignModalOpen.set(true);
    this.loadAvailableUnits(generator.id);
  }

  closeAssignModal(): void {
    this.isAssignModalOpen.set(false);
  }

  onAvailableUnitSearch(value: string): void {
    this.availableUnitSearch.set(value);
    const generator = this.selectedGenerator();
    if (!generator) {
      return;
    }
    this.loadAvailableUnits(generator.id);
  }

  assignUnit(unit: GeneratorUnit): void {
    const generator = this.selectedGenerator();
    if (!generator) {
      return;
    }

    this.generatorUnitMappings.update((mappings) => ({
      ...mappings,
      [generator.id]: [...new Set([...(mappings[generator.id] || []), unit.id])]
    }));

    this.refreshGeneratorCounts(generator.id);
    this.loadAssignedUnits(generator.id);
    this.loadAvailableUnits(generator.id);
    this.loadGenerators(this.currentPage());
    this.showToast('Unit assigned successfully.', 'success');
  }

  removeUnit(unit: GeneratorUnit): void {
    const generator = this.selectedGenerator();
    if (!generator) {
      return;
    }

    this.generatorUnitMappings.update((mappings) => ({
      ...mappings,
      [generator.id]: (mappings[generator.id] || []).filter((id) => id !== unit.id)
    }));

    this.refreshGeneratorCounts(generator.id);
    this.loadAssignedUnits(generator.id);
    this.loadAvailableUnits(generator.id);
    this.loadGenerators(this.currentPage());
    this.showToast('Unit removed successfully.', 'success');
  }

  manageUnitsLink(): string {
    return '/master/generation-units';
  }

  private loadMockData(): void {
    this.http.get<GeneratorMockData>('json/generators-management.mock.json').subscribe({
      next: (mock) => {
        this.header.set(mock.header);
        const state = this.buildLocalState(mock);
        this.allGenerators.set(state.generators);
        this.allUnits.set(state.units);
        this.unitCodeById.set(state.unitCodeById);
        this.generatorUnitMappings.set(state.mappings);
        this.loadGenerators(1);
      },
      error: () => {
        this.header.set(DEFAULT_DATA.header);
        this.allGenerators.set([]);
        this.allUnits.set([]);
        this.generatorUnitMappings.set({});
        this.unitCodeById.set({});
        this.loadGenerators(1);
        this.showToast('Unable to load generator mock data.', 'error');
      }
    });
  }

  private loadGenerators(page: number): void {
    const filtered = this.filteredGenerators();
    const totalItems = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / this.pageSize()));
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const start = (safePage - 1) * this.pageSize();
    const pageItems = filtered.slice(start, start + this.pageSize());

    this.generators.set(pageItems);
    this.currentPage.set(safePage);
    this.totalItems.set(totalItems);
    this.totalPages.set(totalPages);
  }

  private loadAssignedUnits(generatorId: number): void {
    const linkedIds = new Set(this.generatorUnitMappings()[generatorId] || []);
    this.assignedUnits.set(this.allUnits().filter((unit) => linkedIds.has(unit.id)));
  }

  private loadAvailableUnits(generatorId: number): void {
    const linkedIds = new Set(this.generatorUnitMappings()[generatorId] || []);
    const search = this.availableUnitSearch().trim().toLowerCase();
    this.availableUnits.set(
      this.allUnits().filter((unit) => {
        if (linkedIds.has(unit.id)) {
          return false;
        }
        if (!search) {
          return true;
        }
        return [unit.unique_no, unit.model, unit.name_of_developer]
          .some((value) => value.toLowerCase().includes(search));
      })
    );
  }

  private filteredGenerators(): Generator[] {
    const search = this.searchTerm().trim().toLowerCase();
    return this.allGenerators().filter((generator) => {
      if (!search) {
        return true;
      }
      return [
        generator.generator_code,
        generator.name_of_power_plant,
        generator.organization_type,
        generator.source_type,
        generator.parent_company || ''
      ].some((value) => value.toLowerCase().includes(search));
    });
  }

  private buildLocalState(mock: GeneratorMockData): LocalState {
    const generators = mock.rows.map((row, index) => ({
      id: index + 1,
      generator_code: row.code,
      name_of_power_plant: row.name,
      organization_type: row.organizationType,
      source_type: row.sourceType,
      fuel_type: row.fuelType || null,
      parent_company: row.parentCompany || row.companyName || null,
      scada_station_code: row.scadaStationCode || null,
      connecting_substation: row.connectingSubstation || null,
      connecting_voltage_level_kv: row.connectingVoltageLevelKv || null,
      voltage_level_kv: row.voltageLevelKv || null,
      installed_capacity_mw: String(row.installedCapacityMw),
      generator_capacity_mw: String(row.generatorCapacityMw),
      no_of_units: 0,
      log_sheet: null,
      file_path: null,
      other_parameter: row.remarks || null,
      created_at: null,
      updated_at: null,
      is_deleted: false,
      unit_count: 0
    }));

    const generatorIdByCode = Object.fromEntries(generators.map((generator) => [generator.generator_code, generator.id]));
    const generatorIdByName = Object.fromEntries(generators.map((generator) => [generator.name_of_power_plant, generator.id]));

    const units = mock.unitCatalog.map((unit, index) => ({
      id: index + 1,
      unique_no: unit.uniqueNo,
      model: unit.model,
      scada_turbine_gut_inverter_no: unit.code,
      installed_capacity_mw: String(unit.installedCapacityMw),
      latitude: '',
      longitude: '',
      make: unit.status,
      name_of_developer: unit.developerName,
      identifier_tag: unit.code,
      purchase_mode: unit.purchaseMode,
      utility_name: null,
      consumer_utility_name: null,
      consumer_name: null,
      contracted_capacity_mw: null,
      contract_rate_per_kwh: null,
      contract_from_date: unit.commissionDate,
      contract_to_date: null,
      contract_from_time_block: null,
      contract_to_time_block: null,
      remarks: null,
      created_at: null,
      updated_at: null,
      is_deleted: false,
      generator_registration_id: generatorIdByName[unit.generator] || null
    } satisfies GeneratorUnit));

    const unitIdByCode = Object.fromEntries(mock.unitCatalog.map((unit, index) => [unit.code, index + 1]));
    const unitCodeById = Object.fromEntries(mock.unitCatalog.map((unit, index) => [index + 1, unit.code]));
    const mappings: Record<number, number[]> = {};

    for (const generator of generators) {
      const linkedCodes = mock.generatorUnitMappings[generator.generator_code] || [];
      mappings[generator.id] = linkedCodes
        .map((code) => unitIdByCode[code])
        .filter((value): value is number => typeof value === 'number');
    }

    const generatorsWithCounts = generators.map((generator) => {
      const count = mappings[generator.id]?.length || 0;
      return { ...generator, no_of_units: count, unit_count: count };
    });

    return {
      generators: generatorsWithCounts,
      units,
      unitCodeById,
      mappings
    };
  }

  private refreshGeneratorCounts(generatorId: number): void {
    const count = this.generatorUnitMappings()[generatorId]?.length || 0;
    this.allGenerators.update((generators) =>
      generators.map((generator) =>
        generator.id === generatorId
          ? { ...generator, no_of_units: count, unit_count: count }
          : generator
      )
    );
    const selected = this.selectedGenerator();
    if (selected?.id === generatorId) {
      this.selectedGenerator.set({ ...selected, no_of_units: count, unit_count: count });
    }
  }

  private nextGeneratorId(): number {
    return this.allGenerators().reduce((max, generator) => Math.max(max, generator.id), 0) + 1;
  }

  private formatCapacity(value: number): string {
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(value);
  }

  private showToast(message: string | null, type: 'success' | 'error' = 'success'): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
    if (!message) {
      this.toast.set(null);
      return;
    }
    this.toast.set({ message, type });
    this.toastTimer = setTimeout(() => this.toast.set(null), 3000);
  }
}
